"use client"

// Timeline — server-persisted saves per Clerk user (see /api/timeline).
// RSVP labels remain in localStorage until a server model exists.

import type {
  CreateEventData,
  EventData,
  RsvpStatus,
  TimelineSave,
} from "@/lib/events/types"
import {
  getLocalCalendarYmd,
  isPastEventByStoredDate,
} from "@/lib/events/event-archive"
import { createInitialEventsCatalog } from "@/lib/events/seed-catalog"
import {
  calendarYmdStartLocalMs,
  parseIsoDateOnly,
} from "@/lib/events/event-datetime"
import { EVENT_VISIBILITY_PUBLIC } from "@/lib/events/visibility"

export type {
  RsvpStatus,
  TimelineSave,
  EventLink,
  EventContribution,
  EventData,
  CreateEventData,
} from "@/lib/events/types"

export const CURRENT_USER = {
  id: "user_1",
  name: "Demo Host",
  avatar:
    "https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=100&h=100&fit=crop",
}

export const ALL_EVENTS: EventData[] = createInitialEventsCatalog()

/** User-created / server-registered host ids only (no seed “demo hosting” list). */
export const HOSTING_EVENT_IDS: string[] = []

/** Legacy key — migrated to server + `RSVP_STORAGE_KEY` on first signed-in load. */
const TIMELINE_LEGACY_STORAGE_KEY = "maatii_timeline_saves"

const RSVP_STORAGE_KEY = "maatii_timeline_rsvp"

const TIMELINE_MIGRATION_DONE_KEY = "maatii_timeline_server_migrated_v1"

type RsvpEntry = { status: RsvpStatus; updatedAt: string | null }

function readRsvpMap(): Record<string, RsvpEntry> {
  if (typeof window === "undefined") return {}
  try {
    const raw = localStorage.getItem(RSVP_STORAGE_KEY)
    if (!raw) return {}
    const o = JSON.parse(raw) as Record<string, RsvpEntry>
    return o && typeof o === "object" ? o : {}
  } catch {
    return {}
  }
}

function writeRsvpMap(map: Record<string, RsvpEntry>) {
  if (typeof window === "undefined") return
  localStorage.setItem(RSVP_STORAGE_KEY, JSON.stringify(map))
}

/** Read pre-server timeline JSON once for migration. */
export function readLegacyTimelineSavesRaw(): TimelineSave[] {
  if (typeof window === "undefined") return []
  try {
    const stored = localStorage.getItem(TIMELINE_LEGACY_STORAGE_KEY)
    if (!stored) return []
    return JSON.parse(stored).map((save: Record<string, unknown>) => ({
      eventId: String(save.eventId ?? ""),
      savedAt: new Date(String(save.savedAt ?? Date.now())),
      rsvpStatus: (save.rsvpStatus ?? null) as RsvpStatus,
      rsvpUpdatedAt: save.rsvpUpdatedAt
        ? new Date(String(save.rsvpUpdatedAt))
        : null,
    }))
  } catch {
    return []
  }
}

export function migrationAlreadyRan(): boolean {
  if (typeof window === "undefined") return true
  return localStorage.getItem(TIMELINE_MIGRATION_DONE_KEY) === "1"
}

export function markTimelineMigrationDone(): void {
  if (typeof window === "undefined") return
  localStorage.setItem(TIMELINE_MIGRATION_DONE_KEY, "1")
}

/** Copy RSVP fields from legacy saves into `RSVP_STORAGE_KEY`. */
export function migrateRsvpFromLegacySaves(saves: TimelineSave[]): void {
  if (typeof window === "undefined" || saves.length === 0) return
  const map = readRsvpMap()
  for (const s of saves) {
    if (!s.eventId.trim()) continue
    if (s.rsvpStatus) {
      map[s.eventId] = {
        status: s.rsvpStatus,
        updatedAt: s.rsvpUpdatedAt?.toISOString() ?? null,
      }
    }
  }
  writeRsvpMap(map)
}

export function clearLegacyTimelineStorage(): void {
  if (typeof window === "undefined") return
  localStorage.removeItem(TIMELINE_LEGACY_STORAGE_KEY)
}

export function updateRsvp(eventId: string, status: RsvpStatus): boolean {
  const map = readRsvpMap()
  map[eventId] = {
    status,
    updatedAt: new Date().toISOString(),
  }
  writeRsvpMap(map)
  return true
}

/** Drop local timeline RSVP so server-backed `no_response` does not keep a stale label. */
export function clearRsvpStatus(eventId: string): void {
  const id = eventId.trim()
  if (!id) return
  const map = readRsvpMap()
  if (!(id in map)) return
  delete map[id]
  writeRsvpMap(map)
}

export function getRsvpStatus(eventId: string): RsvpStatus {
  return readRsvpMap()[eventId]?.status ?? null
}

export function isHosting(eventId: string): boolean {
  return HOSTING_EVENT_IDS.includes(eventId)
}

export function getEventById(eventId: string): EventData | undefined {
  return ALL_EVENTS.find((e) => e.id === eventId)
}

export function isEventPast(event: EventData): boolean {
  return isPastEventByStoredDate(event.date)
}

export function isEventUpcoming(event: EventData): boolean {
  const raw = event.date?.trim() ?? ""
  if (!raw) return false
  if (!parseIsoDateOnly(raw)) return false
  const eventDay = calendarYmdStartLocalMs(raw)
  const t0 = calendarYmdStartLocalMs(getLocalCalendarYmd())
  if (eventDay == null || t0 == null) return false
  return eventDay >= t0
}

const USER_EVENTS_STORAGE_KEY = "maatii_user_events"

export function getUserCreatedEvents(): EventData[] {
  if (typeof window === "undefined") return []
  try {
    const stored = localStorage.getItem(USER_EVENTS_STORAGE_KEY)
    if (!stored) return []
    return JSON.parse(stored).map((ev: Record<string, unknown>) => ({
      ...ev,
      createdAt: new Date(String(ev.createdAt ?? Date.now())),
    })) as EventData[]
  } catch {
    return []
  }
}

function persistUserEvents(events: EventData[]) {
  if (typeof window === "undefined") return
  localStorage.setItem(USER_EVENTS_STORAGE_KEY, JSON.stringify(events))
}

export function createEvent(data: CreateEventData): EventData {
  const events = getUserCreatedEvents()
  const newEvent: EventData = {
    id: `user_event_${Date.now()}`,
    title: data.title,
    date: data.date,
    time: data.time,
    location: data.location,
    locationName: data.locationName,
    address: data.address,
    lat: data.lat,
    lng: data.lng,
    image: data.image,
    description: data.description,
    hostName: CURRENT_USER.name,
    hostAvatar: CURRENT_USER.avatar,
    attendees: 0,
    isPublished: data.isPublished,
    visibility: data.visibility ?? EVENT_VISIBILITY_PUBLIC,
    links: data.links,
    contributions: data.contributions,
    createdAt: new Date(),
  }

  events.push(newEvent)
  persistUserEvents(events)

  HOSTING_EVENT_IDS.push(newEvent.id)
  ALL_EVENTS.push(newEvent)

  return newEvent
}

export function registerServerCreatedEvent(event: EventData): void {
  const userEvents = getUserCreatedEvents().filter((e) => e.id !== event.id)
  userEvents.push(event)
  persistUserEvents(userEvents)

  if (!HOSTING_EVENT_IDS.includes(event.id)) {
    HOSTING_EVENT_IDS.push(event.id)
  }

  const allIdx = ALL_EVENTS.findIndex((e) => e.id === event.id)
  if (allIdx === -1) {
    ALL_EVENTS.push(event)
  } else {
    ALL_EVENTS[allIdx] = event
  }
}

export function updateEvent(
  eventId: string,
  data: Partial<CreateEventData>,
): EventData | null {
  const userEvents = getUserCreatedEvents()
  const userEventIndex = userEvents.findIndex((e) => e.id === eventId)

  if (userEventIndex !== -1) {
    userEvents[userEventIndex] = {
      ...userEvents[userEventIndex],
      ...data,
    }
    persistUserEvents(userEvents)

    const allIndex = ALL_EVENTS.findIndex((e) => e.id === eventId)
    if (allIndex !== -1) {
      ALL_EVENTS[allIndex] = { ...ALL_EVENTS[allIndex], ...data }
    }

    return userEvents[userEventIndex]
  }

  const staticIndex = ALL_EVENTS.findIndex((e) => e.id === eventId)
  if (staticIndex !== -1 && HOSTING_EVENT_IDS.includes(eventId)) {
    ALL_EVENTS[staticIndex] = {
      ...ALL_EVENTS[staticIndex],
      ...data,
    }
    return ALL_EVENTS[staticIndex]
  }

  return null
}

export function publishEvent(eventId: string): boolean {
  return updateEvent(eventId, { isPublished: true }) !== null
}

export function unpublishEvent(eventId: string): boolean {
  return updateEvent(eventId, { isPublished: false }) !== null
}

export function deleteEvent(eventId: string): boolean {
  const userEvents = getUserCreatedEvents()
  const filteredUserEvents = userEvents.filter((e) => e.id !== eventId)
  if (filteredUserEvents.length !== userEvents.length) {
    persistUserEvents(filteredUserEvents)
  }

  const allIndex = ALL_EVENTS.findIndex((e) => e.id === eventId)
  if (allIndex !== -1) {
    ALL_EVENTS.splice(allIndex, 1)
  }

  const hostingIndex = HOSTING_EVENT_IDS.indexOf(eventId)
  if (hostingIndex !== -1) {
    HOSTING_EVENT_IDS.splice(hostingIndex, 1)
  }

  return true
}

